import React, { FC } from 'react';
import IgBadge from '@components/EntityDrawers/components/ReferencesInputsGroup/components/IgBadge';
import { UnderlineTextButton } from '@components/UnderlineTextButton';
import { Divider } from '@mui/material';
import { useModelFilters } from '@store/zustand/model-filters';
import {
  CisControl,
  controlValueIds,
  selectionCoverage,
  toggleAll,
} from '@utils/cisCatalogue';
import FilterPanelBackTitle from '@views/Project/components/ModelFiltersPopover/components/FilterPanelBackTitle';
import FilterPanelLayout from '@views/Project/components/ModelFiltersPopover/components/FilterPanelLayout';
import FilterValueRow from '@views/Project/components/ModelFiltersPopover/components/FilterValueRow';
import { useSetCategory } from '@views/Project/components/ModelFiltersPopover/useSetCategory';

type Props = {
  control: CisControl;
  counts?: Record<string, number>;
  onBack: () => void;
};

/**
 * One Control's values, rendered beside the CIS-18 reference panel's
 * Controls list as its side panel (AC3.2).
 *
 * Headed by the Control's name, it lists Select all, the Control itself and
 * then its Safeguards, each Safeguard with its IG badge. The Control and
 * its Safeguards are selected independently and each counts as one value:
 * ticking the Control keeps entities that reference the Control itself,
 * never those that reference only its Safeguards (AC6.2). Select all
 * reaches exactly the Control and its Safeguards, reads checked once all
 * of them are selected and indeterminate for some; from all it unselects
 * them. Every write goes through `useSetCategory`, so a tick applies at
 * once and closes a drawer it would hide. "Clear all" appears while any of
 * the Control's values is selected and removes those alone, leaving the
 * rest of the category — AC7.4's "that subcategory only". Counts are
 * rendered only when the container supplies them, on every row but Select
 * all; a value missing from the map reads 0. Its back closes it and leaves
 * the Controls list showing, the same as a click on the open row.
 */
const FilterCisControlPanel: FC<Props> = ({ control, counts, onBack }) => {
  const { cis: selected } = useModelFilters();
  const setCategory = useSetCategory();

  const ids = controlValueIds(control);
  const coverage = selectionCoverage(ids, selected);

  const toggle = (id: string) =>
    setCategory(
      'cis',
      selected.includes(id)
        ? selected.filter((item) => item !== id)
        : [...selected, id],
    );

  const clear = () =>
    setCategory(
      'cis',
      selected.filter((id) => !ids.includes(id)),
    );

  const countOf = (id: string) => (counts ? counts[id] ?? 0 : undefined);

  return (
    <FilterPanelLayout
      title={<FilterPanelBackTitle title={control.label} onBack={onBack} />}
      action={
        coverage !== 'none' && (
          <UnderlineTextButton
            $disabled={false}
            onClick={clear}
            sx={{ flexShrink: 0 }}
          >
            Clear all
          </UnderlineTextButton>
        )
      }
    >
      <FilterValueRow
        label="Select all"
        checked={coverage === 'all'}
        indeterminate={coverage === 'some'}
        onToggle={() => setCategory('cis', toggleAll(selected, ids))}
      />

      <Divider sx={{ borderColor: 'grey.500' }} />

      <FilterValueRow
        label={control.label}
        checked={selected.includes(control.id)}
        count={countOf(control.id)}
        onToggle={() => toggle(control.id)}
      />

      {control.safeguards.map((safeguard) => (
        <FilterValueRow
          key={safeguard.id}
          label={safeguard.label}
          checked={selected.includes(safeguard.id)}
          count={countOf(safeguard.id)}
          endAdornment={
            safeguard.tier ? <IgBadge tier={safeguard.tier} /> : undefined
          }
          onToggle={() => toggle(safeguard.id)}
        />
      ))}
    </FilterPanelLayout>
  );
};

export default FilterCisControlPanel;
